import { Component, OnInit, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplyPanelComponent, CheckboxComponent, ColorPickerComponent, NumberInputComponent } from '@imagise/shared';
import { TranslateModule } from '@ngx-translate/core';
import { Size2D } from '../../../domain/interfaces/shared';
import { MessagingService } from '../../../infrastructure/messaging/messaging.service';
import { RefreshEditOptionsMessage } from '../../../domain/models/messages';
import { EditorFormStore } from '../../../domain';
import { PanelLayoutComponent } from '../panel-layout/panel-layout.component';

const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_TRANSPARENT_BACKGROUND = true;
const MAX_ANIMATION_SIZE = 50000;
const MAX_LOOP_COUNT = 65535;

@Component({
  selector: 'lib-animation-panel',
  host: {
    class: 'contents',
  },
  templateUrl: './animation-panel.component.html',
  imports: [
    ApplyPanelComponent,
    CheckboxComponent,
    ColorPickerComponent,
    NumberInputComponent,
    PanelLayoutComponent,
    TranslateModule,
  ],
})
export class AnimationPanelComponent implements OnInit {
  private readonly messageBus = inject(MessagingService);
  private readonly editorStore = inject(EditorFormStore);

  constructor() {
    this.messageBus
      .of(RefreshEditOptionsMessage).pipe(takeUntilDestroyed()).subscribe(() => this.refresh());
  }

  readonly imageSize = input<Size2D | null>(null);

  readonly apply = output<void>();

  readonly cancelRequested = output<void>();

  width = 1;
  height = 1;
  loopCount = 0;
  backgroundColor = DEFAULT_BACKGROUND_COLOR;
  useTransparentBackground = DEFAULT_TRANSPARENT_BACKGROUND;

  readonly maxAnimationSize = MAX_ANIMATION_SIZE;
  readonly maxLoopCount = MAX_LOOP_COUNT;

  /** The backend builds an animation from two frames minimum. */
  get canApply(): boolean {
    const draft = this.editorStore.getDraft();
    return draft?.kind === 'animation' && draft.params.frames.length > 1;
  }

  ngOnInit(): void {
    this.refresh();
  }

  onWidthChanged(value: number): void {
    this.width = this.normalizeDimension(value);
    this.syncDraft();
  }

  onHeightChanged(value: number): void {
    this.height = this.normalizeDimension(value);
    this.syncDraft();
  }

  onLoopCountChanged(value: number): void {
    this.loopCount = Math.min(MAX_LOOP_COUNT, Math.max(0, Math.round(value || 0)));
    this.syncDraft();
  }

  onTransparentBackgroundChanged(checked: boolean): void {
    this.useTransparentBackground = checked;
    this.syncDraft();
  }

  onBackgroundColorChanged(value: string): void {
    this.backgroundColor = value;
    this.useTransparentBackground = false;
    this.syncDraft();
  }

  onApply(): void {
    this.apply.emit();
  }

  onCancel(): void {
    this.cancelRequested.emit();
  }

  private refresh(): void {
    const draft = this.editorStore.ensureAnimationDraft();
    const size = this.imageSize();

    this.width = this.normalizeDimension(draft?.params.width ?? size?.width ?? 1);
    this.height = this.normalizeDimension(draft?.params.height ?? size?.height ?? 1);
    this.loopCount = Math.min(MAX_LOOP_COUNT, Math.max(0, Math.round(draft?.params.loopCount ?? 0)));
    this.backgroundColor = draft?.params.backgroundColor ?? DEFAULT_BACKGROUND_COLOR;
    this.useTransparentBackground = draft?.params.backgroundColor == null;
    this.syncDraft();
  }

  private syncDraft(): void {
    this.editorStore.updateAnimationDraftSettings({
      width: this.width,
      height: this.height,
      backgroundColor: this.useTransparentBackground ? null : this.backgroundColor,
      loopCount: this.loopCount,
    });
  }

  private normalizeDimension(value: number): number {
    return Math.min(MAX_ANIMATION_SIZE, Math.max(1, Math.round(value || 1)));
  }
}
